import {endOfDay, startOfDay} from 'date-fns';
import {saveAs} from 'file-saver';
import moment from 'moment';
import * as Yup from 'yup';
import {toDutchISOTimestamp} from '../../../util/date';

function fromCommaToPoint(value) {
    let result = value;
    return result.toString().match(/^((0|[1-9][0-9]*)(?:[\,]\d*)?)$/) ? result.toString().replace(',', '.') : result;
}

/**
 * Gets today's date with time 00:00:00
 * @returns {Date} today's date with time 00:00:00
 */
function getDateToday() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(today);
}

/**
 * Gets the day in one week from the provided startDate
 * @param {Date} startDate day from which one week should be counted
 * @returns {Date} Date one week from startDate with time 23:59:59
 */
function getDateOfSameDayNextWeek(startDate) {
    const date = new Date(startDate);
    const nextWeek = new Date();
    nextWeek.setDate(date.getDate() + 7);
    nextWeek.setHours(23, 59, 59, 0);
    return new Date(nextWeek);
}

function validatedValues(values) {
    let safeValues = _.cloneDeep(values);

    const validValues = {
        customerId: safeValues.customerId,
        name: safeValues.name,
        plantName: safeValues.plantName,
        invoiceLogicType: safeValues.invoiceLogicType,
        priceLaborSaturdaySupervisor: fromCommaToPoint(safeValues.priceLaborSaturdaySupervisor),
        priceLaborSundayHolidaySupervisor: fromCommaToPoint(safeValues.priceLaborSundayHolidaySupervisor),
        priceLaborSaturdayForklift: fromCommaToPoint(safeValues.priceLaborSaturdayForklift),
        priceLaborSundayHolidayForklift: fromCommaToPoint(safeValues.priceLaborSundayHolidayForklift),
        surchargeInHandlingSat: safeValues.surchargeInHandlingSat,
        surchargeInHandlingSun: safeValues.surchargeInHandlingSun,
        surchargeOutHandlingSat: safeValues.surchargeOutHandlingSat,
        surchargeOutHandlingSun: safeValues.surchargeOutHandlingSun,

        articleGroups: _.map(safeValues.articleGroups, (ag) => ({
            id: ag.id,
            name: ag.name,
            articleGroupType: ag.articleGroupType,
            startDate: toDutchISOTimestamp(ag.startDate, startOfDay),
            endDate: toDutchISOTimestamp(ag.endDate, endOfDay),
            fixedQuantity: fromCommaToPoint(ag.fixedQuantity),
            variableQuantity: fromCommaToPoint(ag.variableQuantity),
            pricePerPalletFixed: fromCommaToPoint(ag.pricePerPalletFixed),
            pricePerPalletVariable: fromCommaToPoint(ag.pricePerPalletVariable),
            pricePerPalletHandlingIn: fromCommaToPoint(ag.pricePerPalletHandlingIn),
            pricePerPalletHandlingOut: fromCommaToPoint(ag.pricePerPalletHandlingOut),
            articleType: ag.articleType,
        })),

        extraWorkItems: _.map(safeValues.extraWorkItems, (ewi) => ({
            id: ewi.id,
            description: ewi.description,
            startDate: toDutchISOTimestamp(ewi.startDate, startOfDay),
            endDate: toDutchISOTimestamp(ewi.endDate, endOfDay),
            pricePerUnit: fromCommaToPoint(ewi.pricePerUnit),
            unit: ewi.unit,
        })),
    };

    return validValues;
}

/**
 * Check if something is a number where:
 * valid:       1,1     1.1
 * not valid:   01      1.1,1
 * @param {string} value potential number to be checked
 * @returns {boolean}
 */
const isNumberGreaterOrEqualZero = (value) => /^((0|[1-9][0-9]*)(?:[\.\,]\d*)?|)$/.test(value);
const isIntegerGreaterOrEqualZero = (value) => /^(0|[1-9][0-9]*)$/.test(value);

const isEarlierThanEarliest = (value, testContext) => {
    return (
        !testContext.parent.hasLinkedStockData ||
        moment(value).isSameOrBefore(moment(testContext.parent.earliestDateLinkedToAG))
    );
};

const isLaterThanLatest = (value, testContext) => {
    return (
        !testContext.parent.hasLinkedStockData ||
        moment(value).isSameOrAfter(moment(testContext.parent.latestDateLinkedToAG))
    );
};

/**
 * Retrieves the article group from the given array of article groups at the given validation context path.
 * @param articleGroups {Array} Array of article groups.
 * @param path {string} Path to a field within the article group that is to be extracted in the format of `"articleGroups[5].startDate"`.
 */
const getArticleGroup = (articleGroups, path) => articleGroups[extractArticleGroupIndex(path)];

/**
 * Extracts the index of the article group item from the given validation context path.
 * @param path {string} Path to the current field in the format of `"articleGroups[5].startDate"`.
 */
const extractArticleGroupIndex = (path) => Number(path.match(/\[(\d+)\]/)[1]);

/**
 * Retrieves the article groups of the given type from the given array of article groups, excluding the article group at the given index.
 * @param articleGroups {Array} Array of article groups.
 * @param articleGroupType {string} The type article groups that are to be extracted.
 * @param exceptIndex {number} The index of the article group that is to be excluded from the result.
 */
const getArticleGroupsOfType = (articleGroups, articleGroupType, exceptIndex) =>
    _.filter(articleGroups, (ag, index) => ag.articleGroupType === articleGroupType && index !== exceptIndex);

/**
 * Finds the first article group that overlaps with the given start and end dates. An article group is considered to overlap with
 * the given dates in the following cases.
 *
 * **CASE** The start date (`s`) is within the period of an article group:
 * ```
 * AG: -[==========]-
 *       s   s     s
 * ```
 *
 * **CASE** The end date (`e`) is within the period of an article group:
 * ```
 * AG: -[==========]-
 *       e   e     e
 * ```
 *
 * **CASE** The start (`s`) and end (`e`) dates define a period that includes an entire article group:
 * ```
 * AG: -----[======]----
 *       s============e
 * ```
 *
 * @param startDate {Date} The start date.
 * @param endDate {Date?} The end date. If not provided, the start date is considered to be both the start and end date.
 * @param articleGroups {Array} Array of article groups.
 */
const findOverlappingArticleGroup = (startDate, endDate, articleGroups) => {
    // if we are here we assume that start date is valid in all senses,
    // and articleGroups.length > 0

    const period = {startDate, endDate: _.defaultTo(endDate, startDate)};
    const sortedArticleGroups = _.sortBy(articleGroups, (ag) => moment(ag.startDate));
    return _.find(sortedArticleGroups, (ag) => {
        // Check case 1: start date is within the period of an article group
        if (moment(period.startDate).isBetween(moment(ag.startDate), moment(ag.endDate), 'day', '[]')) return true;

        // Check case 2: end date is within the period of an article group
        if (moment(period.endDate).isBetween(moment(ag.startDate), moment(ag.endDate), 'day', '[]')) return true;

        // Check case 3: start and end dates define a period that includes an entire article group
        if (
            moment(ag.startDate).isBetween(moment(period.startDate), moment(period.endDate), 'day', '[]') &&
            moment(ag.endDate).isBetween(moment(period.startDate), moment(period.endDate), 'day', '[]')
        )
            return true;
    });
};

/**
 * Checks whether the given date is overlapping with any article group of the given type in the given array of article groups.
 * @param startDate {Date} The start date of the period to check for overlapping article groups.
 * @param endDate {Date} The end date of the period to check for overlapping article groups.
 * @param articleGroupType {string} The type of article groups to check for overlapping.
 * @param articleGroups {Array} Array of article groups.
 * @returns {boolean} True if the date is overlapping with an article group of the given type, false otherwise.
 */
const isPeriodNotOverlappingWithArticleGroups = (startDate, endDate, articleGroupType, articleGroups) => {
    return (
        articleGroupType === 'Default' ||
        _.isEmpty(articleGroups) ||
        _.isNil(findOverlappingArticleGroup(startDate, endDate, articleGroups))
    );
};

const getExtraWorkItem = (extraWorkItems, path, field) => {
    let n = path.replace('extraWorkItems[', '').replace('].' + field, '');
    return extraWorkItems[n];
};

const isEarlierThanEarliestEntry = (value, testContext) => {
    return (
        !testContext.parent.isLinkedExtraWorkItem ||
        moment(value).isSameOrBefore(moment(testContext.parent.earliestEntryDateForEWI))
    );
};

const isLaterThanLatestEntry = (value, testContext) => {
    return (
        !testContext.parent.isLinkedExtraWorkItem ||
        moment(value).isSameOrAfter(moment(testContext.parent.latestEntryDateForEWI))
    );
};

const validationSchema = (t, customer) =>
    Yup.object().shape({
        // Header validation rules
        name: Yup.string().required(t('errors.RequiredField')),
        invoiceLogicType: Yup.string().required(t('errors.RequiredField')),
        priceLaborSaturdaySupervisor: Yup.string()
            .required(t('errors.RequiredField'))
            .test('is Number >= 0', t('errors.MustBeNumberGreaterOrEqualZero'), isNumberGreaterOrEqualZero),
        priceLaborSundayHolidaySupervisor: Yup.string()
            .required(t('errors.RequiredField'))
            .test('is Number >= 0', t('errors.MustBeNumberGreaterOrEqualZero'), isNumberGreaterOrEqualZero),
        priceLaborSaturdayForklift: Yup.string()
            .required(t('errors.RequiredField'))
            .test('is Number >= 0', t('errors.MustBeNumberGreaterOrEqualZero'), isNumberGreaterOrEqualZero),
        priceLaborSundayHolidayForklift: Yup.string()
            .required(t('errors.RequiredField'))
            .test('is Number >= 0', t('errors.MustBeNumberGreaterOrEqualZero'), isNumberGreaterOrEqualZero),
        surchargeInHandlingSat: Yup.string()
            .required(t('errors.RequiredField'))
            .test('is Integer >= 0', t('errors.MustBeIntegerGreaterOrEqualZero'), isIntegerGreaterOrEqualZero),
        surchargeInHandlingSun: Yup.string()
            .required(t('errors.RequiredField'))
            .test('is Integer >= 0', t('errors.MustBeIntegerGreaterOrEqualZero'), isIntegerGreaterOrEqualZero),
        surchargeOutHandlingSat: Yup.string()
            .required(t('errors.RequiredField'))
            .test('is Integer >= 0', t('errors.MustBeIntegerGreaterOrEqualZero'), isIntegerGreaterOrEqualZero),
        surchargeOutHandlingSun: Yup.string()
            .required(t('errors.RequiredField'))
            .test('is Integer >= 0', t('errors.MustBeIntegerGreaterOrEqualZero'), isIntegerGreaterOrEqualZero),

        // Article group cards validation rules
        articleGroups: Yup.array().of(
            Yup.object().shape({
                name: Yup.string().required(t('errors.RequiredField')),
                articleGroupType: Yup.string().required(t('errors.RequiredField')),
                startDate: Yup.date()
                    .required()
                    // we whant to validate start date for overlapping with other the same type AG
                    // for this we need to know the AG type, and if we just created the AG od ust changed the type,
                    // we need to read from the field "articleGroupType".
                    // in Yup you can approach other field of validated object with .when()
                    .when(
                        'articleGroupType',
                        (articleGroupType, schema) =>
                            articleGroupType &&
                            schema.test({
                                name: 'testForStartIfThisAGTypeOverlapping',
                                /**
                                 * Validates the start date of the article group for overlapping with other article groups of the same type.
                                 * @param {Date} value The start date being validated.
                                 * @param {object} testContext The validation context.
                                 * @param {string} testContext.path The path to the field being validated, eg. "articleGroups[5].startDate".
                                 * @param {object} testContext.parent The article group object being validated.
                                 * @param {Array} testContext.from The array of parent objects.
                                 * @param {object} testContext.from[0].value The parent article group object.
                                 * @param {object} testContext.from[1].value The parent customer object.
                                 */
                                test: (value, testContext) => {
                                    // get all article groups of the same type except the one we are validating now
                                    const articleGroupsExceptCurrent = getArticleGroupsOfType(
                                        testContext.from[1].value.articleGroups,
                                        articleGroupType,
                                        extractArticleGroupIndex(testContext.path),
                                    );

                                    // consider the start date as valid if there are no other article groups of the same type
                                    const isValid = isPeriodNotOverlappingWithArticleGroups(
                                        value,
                                        null,
                                        articleGroupType,
                                        articleGroupsExceptCurrent,
                                    );

                                    if (!isValid) {
                                        const overlappingAG = findOverlappingArticleGroup(
                                            value,
                                            null,
                                            articleGroupsExceptCurrent,
                                        );
                                        return testContext.createError({
                                            message: t('errors.ThisDateOverlapWithAG', {
                                                OtherAGName: overlappingAG.name,
                                            }),
                                        });
                                    }

                                    return true;
                                },
                            }),
                    )
                    .test({
                        name: 'isTooLateAG',
                        params: {customer},
                        message: (value) =>
                            t('errors.CouldNotChangeArticleGroupStartDate', {
                                AGName: getArticleGroup(customer.articleGroups, value.path).name,
                                earliestDateLinkedToAG: moment(
                                    getArticleGroup(customer.articleGroups, value.path).earliestDateLinkedToAG,
                                ).format('DD-MM-YYYY'),
                            }),
                        test: isEarlierThanEarliest,
                    }),
                endDate: Yup.date()
                    .test('isLaterAG', t('errors.EndTimeMustBeLater'), (value, testContext) => {
                        return testContext.parent.startDate < value;
                    })
                    // we whant to validate end date for overlapping with other the same type AG
                    // for this we need to know the AG type, and if we just created the AG od ust changed the type,
                    // we need to read from the field "articleGroupType".
                    // in Yup you can approach other field of validated object with .when()
                    .when(
                        ['articleGroupType', 'startDate'],
                        (articleGroupType, startDate, schema) =>
                            articleGroupType &&
                            schema.test({
                                name: 'testFForEndDateIfThisAGTypeOverlapping',
                                /**
                                 * Validates the end date of the article group for overlapping with other article groups of the same type.
                                 * @param {Date} value The start date being validated.
                                 * @param {object} testContext The validation context.
                                 * @param {string} testContext.path The path to the field being validated, eg. "articleGroups[5].startDate".
                                 * @param {object} testContext.parent The article group object being validated.
                                 * @param {Array} testContext.from The array of parent objects.
                                 * @param {object} testContext.from[0].value The parent article group object.
                                 * @param {object} testContext.from[1].value The parent customer object.
                                 */
                                test: (value, testContext) => {
                                    const articleGroupsExceptCurrent = getArticleGroupsOfType(
                                        testContext.from[1].value.articleGroups,
                                        articleGroupType,
                                        extractArticleGroupIndex(testContext.path),
                                    );
                                    const isValid = isPeriodNotOverlappingWithArticleGroups(
                                        startDate,
                                        value,
                                        articleGroupType,
                                        articleGroupsExceptCurrent,
                                    );

                                    if (!isValid)
                                        return testContext.createError({
                                            message: t('errors.ThisDateOverlapWithAG', {
                                                OtherAGName: findOverlappingArticleGroup(
                                                    startDate,
                                                    value,
                                                    articleGroupsExceptCurrent,
                                                ).name,
                                            }),
                                        });

                                    return isValid;
                                },
                            }),
                    )
                    .test({
                        name: 'isTooLateAG',
                        params: {customer},
                        message: (value) =>
                            t('errors.CouldNotChangeArticleGroupEndDate', {
                                AGName: getArticleGroup(customer.articleGroups, value.path).name,
                                latestDateLinkedToAG: moment(
                                    getArticleGroup(customer.articleGroups, value.path).latestDateLinkedToAG,
                                ).format('DD-MM-YYYY'),
                            }),
                        test: isLaterThanLatest,
                    })
                    .test('isTooEarlyAG', t('errors.CouldNotChangeArticleGroupEndDate'), isLaterThanLatest),
                fixedQuantity: Yup.string()
                    .required(t('errors.RequiredField'))
                    .test('is integer >= 0', t('errors.MustBeIntegerGreaterOrEqualZero'), isIntegerGreaterOrEqualZero),
                variableQuantity: Yup.string()
                    .required(t('errors.RequiredField'))
                    .test('is integer >= 0', t('errors.MustBeIntegerGreaterOrEqualZero'), isIntegerGreaterOrEqualZero),
                pricePerPalletFixed: Yup.string()
                    .required(t('errors.RequiredField'))
                    .test('is Number >= 0', t('errors.MustBeNumberGreaterOrEqualZero'), isNumberGreaterOrEqualZero),
                pricePerPalletVariable: Yup.string()
                    .required(t('errors.RequiredField'))
                    .test('is Number >= 0', t('errors.MustBeNumberGreaterOrEqualZero'), isNumberGreaterOrEqualZero),
                pricePerPalletHandlingIn: Yup.string()
                    .required(t('errors.RequiredField'))
                    .test('is Number >= 0', t('errors.MustBeNumberGreaterOrEqualZero'), isNumberGreaterOrEqualZero),
                pricePerPalletHandlingOut: Yup.string()
                    .required(t('errors.RequiredField'))
                    .test('is Number >= 0', t('errors.MustBeNumberGreaterOrEqualZero'), isNumberGreaterOrEqualZero),
            }),
        ),

        // Extra Work Item cards validation rules
        extraWorkItems: Yup.array().of(
            Yup.object().shape({
                description: Yup.string().required(t('errors.RequiredField')),
                startDate: Yup.date()
                    .nullable()
                    .required()
                    .test({
                        name: 'isTooLateEW',
                        params: {customer},
                        message: (value) =>
                            t('errors.CouldNotChangeExtraWorkItemStartDate', {
                                EWIDescription: getExtraWorkItem(customer.extraWorkItems, value.path, 'startDate')
                                    .description,
                                eariestEntryDate: moment(
                                    getExtraWorkItem(customer.extraWorkItems, value.path, 'startDate')
                                        .earliestEntryDateForEWI,
                                ).format('DD-MM-YYYY'),
                            }),
                        test: isEarlierThanEarliestEntry,
                    }),
                endDate: Yup.date()
                    .test('isLaterEW', t('errors.EndTimeMustBeLater'), (value, testContext) => {
                        return testContext.parent.startDate < value;
                    })
                    .test({
                        name: 'sTooEarlyEW',
                        params: {customer},
                        message: (value) =>
                            t('errors.CouldNotChangeExtraWorkItemEndDate', {
                                EWIDescription: getExtraWorkItem(customer.extraWorkItems, value.path, 'endDate')
                                    .description,
                                latestEntryDate: moment(
                                    getExtraWorkItem(customer.extraWorkItems, value.path, 'endDate')
                                        .latestEntryDateForEWI,
                                ).format('DD-MM-YYYY'),
                            }),
                        test: isLaterThanLatestEntry,
                    })
                    .test('isTooEarlyEW', t('errors.CouldNotChangeExtraWorkItemEndDate'), isLaterThanLatestEntry),
                pricePerUnit: Yup.string()
                    .required(t('errors.RequiredField'))
                    .test('is Number >= 0', t('errors.MustBeNumberGreaterOrEqualZero'), isNumberGreaterOrEqualZero),
                unit: Yup.string().required(t('errors.RequiredField')),
            }),
        ),
    });

const createExportDataSchema = (t) =>
    Yup.object().shape({
        timestampFrom: Yup.mixed().required(t('errors.RequiredField')),
        timestampTo: Yup.mixed()
            .required(t('errors.RequiredField'))
            .when(
                'timestampFrom',
                /**
                 * Registers a new test that checks whether the `timestampTo` field is later than the `timestampFrom` field.
                 * @param {moment.Moment} timestampFrom The value of the `timestampFrom` field.
                 * @param {Yup.ObjectSchema} schema The schema object.
                 */
                (timestampFrom, schema) =>
                    schema.test({
                        name: 'timestampToShouldBeLaterThanFrom',
                        /**
                         * Checks whether the `timestampTo` field is later than the `timestampFrom` field.
                         * @param {moment.Moment} value The value of the `timestampTo` field.
                         * @param {Yup.TestContext} context The context object.
                         */
                        test: (value, context) => {
                            if (!value) {
                                return context.createError({message: t('errors.RequiredField')});
                            }
                            if (!value.isAfter(timestampFrom)) {
                                return context.createError({message: t('errors.EndTimeMustBeLater')});
                            }

                            return true;
                        },
                    }),
            ),
    });

const getFileNameFromHeader = (headers) =>
    // raw header looks like: 'attachment; filename=<filename-here>; [optional parts]'
    headers
        .split(';')
        .map((item) => item.split('=').map((x) => x.trim()))
        .find(([key]) => key === 'filename')?.[1]
        // if the file name contains spaces, it will be wrapped in double quotes here;
        // the quotes in turn get converted to _ by the browser, resulting in the
        // downloaded file having an extension of ".xlsx_" making it unusable
        // eg. "file name.xlsx" -> _file name.xlsx_
        ?.replace(/"/g, '');

function downloadFileFromAxiosResponse(response) {
    saveAs(response.data, getFileNameFromHeader(response.headers['content-disposition']) ?? 'download.xlsx', {
        type: response.data.type,
    });
}

export {
    fromCommaToPoint,
    validatedValues,
    validationSchema,
    getDateToday,
    getDateOfSameDayNextWeek,
    createExportDataSchema,
    downloadFileFromAxiosResponse,
};
